import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickEntry from "@/pages/home/components/CategoryQuickEntry";
import SectionHeading from "@/components/base/SectionHeading";
import MediaCard from "@/components/base/MediaCard";
import NewsCard from "@/components/base/NewsCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movieList } from "@/mocks/movies";
import { tvList } from "@/mocks/tvshows";
import { newsList } from "@/mocks/news";
import type { MediaItem, NewsItem } from "@/types";

const movieGenres = [
  { label: "动作", genre: "动作", icon: "ri-sword-line", desc: "热血动作" },
  { label: "喜剧", genre: "喜剧", icon: "ri-emotion-happy-line", desc: "轻松解压" },
  { label: "剧情", genre: "剧情", icon: "ri-book-open-line", desc: "深度叙事" },
  { label: "科幻", genre: "科幻", icon: "ri-planet-line", desc: "未来想象" },
  { label: "悬疑", genre: "悬疑", icon: "ri-eye-line", desc: "烧脑反转" },
  { label: "爱情", genre: "爱情", icon: "ri-heart-line", desc: "浪漫治愈" },
];

const tvGenres = [
  { label: "古装", genre: "古装", icon: "ri-ancient-gate-line", desc: "历史传奇" },
  { label: "都市", genre: "都市", icon: "ri-building-line", desc: "都市生活" },
  { label: "悬疑", genre: "悬疑", icon: "ri-fingerprint-line", desc: "刑侦推理" },
  { label: "仙侠", genre: "仙侠", icon: "ri-cloud-line", desc: "仙途问道" },
  { label: "治愈", genre: "治愈", icon: "ri-leaf-line", desc: "温暖治愈" },
  { label: "校园", genre: "校园", icon: "ri-graduation-cap-line", desc: "青春成长" },
];

const movies = movieList as MediaItem[];
const tvshows = tvList as MediaItem[];
const news = newsList as NewsItem[];

const stats = [
  { value: "12,800+", label: "收录影视作品" },
  { value: "3,200+", label: "每日更新资讯" },
  { value: "28 类", label: "类型精细分类" },
  { value: "全端", label: "电脑手机自适应" },
];

export default function Home() {
  usePageMeta({
    title: "四虎视频-专业影视内容平台，丰富电影电视剧资源",
    description:
      "四虎视频致力于为用户提供丰富的电影、电视剧和热门影视资讯，涵盖多种类型和最新内容，方便用户浏览与影视相关信息。",
    keywords:
      "四虎视频,影视内容,电影资源,电视剧推荐,在线观看影视,影视资讯,影视导航,影视平台",
  });

  const hotList = [...movies, ...tvshows].sort((a, b) => b.rating - a.rating).slice(0, 8);

  return (
    <div className="w-full">
      <HeroCarousel />

      <section className="border-b border-background-200/70 bg-background-100/60">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-2 gap-4 px-4 py-8 md:grid-cols-4 md:px-6">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-heading text-2xl font-bold text-primary-700 md:text-3xl">
                {stat.value}
              </p>
              <p className="mt-1 text-xs text-foreground-600 md:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <CategoryQuickEntry
        title="电影分类快捷入口"
        subtitle="按类型快速进入电影专区，发现你想看的每一部作品"
        basePath="/category/movie"
        items={movieGenres}
      />

      <div className="bg-background-100/50">
        <CategoryQuickEntry
          title="电视剧分类快捷入口"
          subtitle="热播剧集一网打尽，追剧从此不再迷路"
          basePath="/category/tv"
          items={tvGenres}
        />
      </div>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <SectionHeading
          title="最新影视资讯精选"
          subtitle="掌握行业动态、深度影评与新片预告"
          moreLabel="更多资讯"
          morePath="/news"
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {news.slice(0, 4).map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="bg-background-100/50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <SectionHeading
            title="热门影视推荐"
            subtitle="电影与剧集混合推荐，高口碑佳作一次看个够"
            moreLabel="进入电影分类"
            morePath="/category/movie"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {hotList.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-col items-center justify-between gap-6 rounded-lg bg-primary-900 px-6 py-10 text-center md:flex-row md:text-left md:px-12">
          <div>
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              想找的作品找不到？用关键词一搜即得
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-primary-100/80">
              支持按片名、演员、类型等关键词检索影视作品与影视资讯，快速定位你感兴趣的内容。
            </p>
          </div>
          <Link
            to="/search"
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold whitespace-nowrap text-primary-950 transition-colors hover:bg-accent-400"
          >
            <i className="ri-search-line text-lg" />
            前往搜索
          </Link>
        </div>
      </section>
    </div>
  );
}